import { useRef } from 'react';

import type { AsyncDataReloaderT } from '@dr.pogodin/react-global-state';
import { time } from '@dr.pogodin/react-utils';

import { getExperience } from 'services/experience';
import type { ExperienceAndSessionMetaT } from 'utils';
import { initExperienceData, useAsyncData } from 'utils/global-state';

type SetT = (e: ExperienceAndSessionMetaT) => void;

type HeapT = {
  set: SetT;
};

export type UseExperienceResT = {
  busy: boolean;
  data: ExperienceAndSessionMetaT | null;
  reload: AsyncDataReloaderT<ExperienceAndSessionMetaT>;
  set: SetT;
};

export function useExperience(
  // BEWARE: This optional argument is currently intended for development
  // use only (like to load specific experience from DB for testing purposes).
  id?: string,
): UseExperienceResT {
  const e = useAsyncData(
    'experience',
    (): ExperienceAndSessionMetaT | Promise<ExperienceAndSessionMetaT> => {
      if (id === undefined) {
        return {
          checkInId: null,
          experience: initExperienceData(),
          sessionKind: null,
        };
      }

      return getExperience(id).then((experience) => ({
        checkInId: null,
        experience,
        sessionKind: null,
      }));
    },
    {
      deps: [id],
      maxage: time.YEAR_MS,
    },
  );

  const { current: heap } = useRef<HeapT>({
    // TODO: This is temporary, to have set() as a stable function,
    // later it will be available directly from the "react-global-state" lib:
    // https://github.com/birdofpreyru/react-global-state/issues/115
    set: (value: ExperienceAndSessionMetaT) => {
      void e.reload(() => value);
    },
  });

  // eslint-disable-next-line react-hooks/refs
  return {
    busy: e.loading,
    data: e.data,
    reload: e.reload,

    // eslint-disable-next-line react-hooks/refs
    set: heap.set,
  };
}
